import { Text, Flex, Skeleton, Heading, useMatchBreakpoints, BalanceWithLoading, Pool, Button, FlexGap, useModal } from '@pancakeswap/uikit'
import { getCakeVaultProfit } from 'views/Pools/helpers'
import { useTranslation } from '@pancakeswap/localization'
import { useVaultPoolByKey } from 'state/pools/hooks'
import { DeserializedLockedCakeVault, IsCakeVault, IsLocked } from 'state/types'
import { getVaultPosition, VaultPosition } from 'utils/cakePool'
import { useVaultApy } from 'hooks/useVaultApy'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { Token } from '@pancakeswap/sdk'

import { ActionContainer, ActionTitles, ActionContent } from './styles'
import UnstakingFeeCountdownRow from '../../CakeVaultCard/UnstakingFeeCountdownRow'
import useUserDataInVaultPresenter from '../../LockedPool/hooks/useUserDataInVaultPresenter'
import HarvestModal from '../../Modals/HarvestModal'

const AutoHarvestAction: React.FunctionComponent<React.PropsWithChildren<Pool.DeserializedPool<Token>>> = ({
  userDataLoaded,
  earningTokenPrice,
  vaultKey,
}) => {
  const { t } = useTranslation()
  const { account, chainId } = useActiveWeb3React()
  const { isMobile } = useMatchBreakpoints()

  const vaultData = useVaultPoolByKey(vaultKey)
  const {
    userData: { pendingProfit },
  } = vaultData

  const isCakeVault = IsCakeVault(vaultKey)
  const { hasAutoEarnings, autoCakeToDisplay, autoUsdToDisplay } = getCakeVaultProfit(
    account, pendingProfit, 
    earningTokenPrice,
    IsLocked(vaultKey)
      ? (vaultData as DeserializedLockedCakeVault).userData.currentPerformanceFee
          .plus((vaultData as DeserializedLockedCakeVault).userData.currentOverdueFee)
      : null,
  )

  const { secondDuration, weekDuration } = useUserDataInVaultPresenter({
    lockStartTime:
      IsLocked(vaultKey) ? (vaultData as DeserializedLockedCakeVault).userData?.lockStartTime ?? '0' : '0',
    lockEndTime:
      IsLocked(vaultKey) ? (vaultData as DeserializedLockedCakeVault).userData?.lockEndTime ?? '0' : '0',
  })

  const { boostFactor } = useVaultApy({ chainId, duration: secondDuration })

  const vaultPosition = getVaultPosition(vaultData.userData)

  const isLocked = IsLocked(vaultKey) && (vaultData as DeserializedLockedCakeVault).userData.locked

  const [onPresentCollect] = useModal(
    <HarvestModal
      vaultKey={vaultKey}
      formattedBalance={autoCakeToDisplay.toFixed(5)}
      fullBalance={autoCakeToDisplay.toFixed(5)}
      earningTokenSymbol="BBC"
      earningsDollarValue={autoUsdToDisplay}
    />
  )

  const actionTitle = (
    <Text fontSize="12px" bold color="secondary" as="span" textTransform="uppercase">
      {t('Recent BBC profit')}
    </Text>
  )

  if (!account) {
    return (
      <ActionContainer>
        <ActionTitles>{actionTitle}</ActionTitles>
        <ActionContent>
          <Heading>0</Heading>
        </ActionContent>
      </ActionContainer>
    )
  }

  if (!userDataLoaded) {
    return (
      <ActionContainer>
        <ActionTitles>{actionTitle}</ActionTitles>
        <ActionContent>
          <Skeleton width={180} height="32px" marginTop={14} />
        </ActionContent>
      </ActionContainer>
    )
  }

  return (
    <ActionContainer justifyContent="space-between">
      <FlexGap gap="10px">
        <Flex flexDirection="column" flex="0 0 50%">
          <ActionTitles>{actionTitle}</ActionTitles>
          <ActionContent mt="8px">
            <Flex flexDirection="column" alignSelf="flex-start">
              <BalanceWithLoading lineHeight="1" bold fontSize="20px" decimals={5} value={autoCakeToDisplay} />
              {Number.isFinite(earningTokenPrice) && earningTokenPrice > 0 && (
                <BalanceWithLoading
                  display="inline"
                  fontSize="12px"
                  color="textSubtle"
                  decimals={2}
                  prefix="~"
                  value={autoUsdToDisplay}
                  unit=" USD"
                />
              )}                
            </Flex>
          </ActionContent>
        </Flex>        
        {[VaultPosition.Flexible, VaultPosition.None].includes(vaultPosition) && (
          <Flex flexDirection="column" alignSelf="flex-start" alignItems="flex-start" flex="0 0 50%">
            <UnstakingFeeCountdownRow vaultKey={vaultKey} />
          </Flex>
        )}
        {!isMobile && isLocked && (
          <Flex flexDirection="column" flex="0 0 50%">
            <ActionTitles>
              <Text fontSize="12px" bold color="secondary" as="span" textTransform="uppercase">
                {t('Yield boost')}
              </Text>
            </ActionTitles>
            <ActionContent mt="8px">
              <Flex flex="1" flexDirection="column" alignSelf="flex-start">
                <BalanceWithLoading
                  color="text"
                  lineHeight="1"
                  bold
                  fontSize="20px"
                  value={boostFactor ? boostFactor?.toString() : '0'}
                  decimals={2}
                  unit="x"
                />
                <Text fontSize="12px" color="textSubtle">
                  {t('Lock for %duration%', { duration: weekDuration })}
                </Text>
              </Flex>
            </ActionContent>
          </Flex>
        )}
      </FlexGap>      
      { 
        !isCakeVault && hasAutoEarnings && !isLocked &&
        <Button variant="secondary" mt="0.5em" onClick={onPresentCollect}>Harvest</Button>
      }      
    </ActionContainer>
  )
}

export default AutoHarvestAction
